import { Modal } from 'bootstrap';
import {fetchModal} from "../api/modals";

const DEFAULT_MODAL_ID = 'vincarioModal';
const ERROR_MODAL_NOT_FOUND = 'Modal not found';
const modalCache = {};
let loadingTimeout;

/**
 * Asynchronously loads and displays a modal based on the specified modal type.
 *
 * @param {string} modalType - The type of modal to be loaded (e.g., 'login', 'register').
 * @param {Object} [options] - Optional settings for the modal.
 * @param {string} [options.modalId] - The ID to use for the modal element. Defaults to 'vincarioModal'.
 * @param {Function} [options.onLoad] - An optional function to be called after the modal is loaded and displayed.
 * @param {Function} [options.onError] - An optional function to be called if the modal fails to load.
 * @returns {Promise<void>} - A promise that resolves when the modal has been loaded and displayed.
 * @throws {Error} - Throws an error if the modal could not be loaded.
 */
export async function loadAndShowModal(modalType, options = {}) {
    const modalId = options.modalId || DEFAULT_MODAL_ID;
    const onLoad = options.onLoad || null;
    const onError = options.onError || null;
    const size = options.size || 'modal-md';
    const queryParams = options.queryParams || {};
    const modalKey = generateModalKey(modalType, size, queryParams);

    let existingModal = document.getElementById(modalId);

    if (existingModal) {
        if (existingModal.dataset.modalKey === modalKey) {
            if (onLoad && typeof onLoad === 'function') {
                await onLoad();
            }
            showModal(modalId);
            return;
        }

        removeExistingModal(modalId);
        existingModal = null;
    }

    try {

        let modalHtml = modalCache[modalKey];
        if (!modalHtml) {
            showLoadingOverlayWithDelay(300);
            modalHtml = await fetchModal(modalType, size, queryParams);
            modalCache[modalKey] = modalHtml;
        }

        insertModalHtml(modalHtml);

        const newModal = document.getElementById(modalId);
        if (newModal) {
            newModal.dataset.modalKey = modalKey;
        }

        if (onLoad && typeof onLoad === 'function') {
            await onLoad();
        }

        showModal(modalId);
    } catch (error) {
        console.error('Error loading modal:', error);
        if (onError && typeof onError === 'function') {
            await onError();
        }
    } finally {
        hideLoadingOverlay();
    }
}

/**
 *
 * @param modalType
 * @param size
 * @param queryParams
 * @returns {string}
 */
function generateModalKey(modalType, size, queryParams) {
    const str = JSON.stringify({ modalType, size, queryParams });
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const chr = str.charCodeAt(i);
        hash = (hash << 5) - hash + chr;
        hash |= 0; // Convert to 32bit integer
    }
    return hash.toString();
}

/**
 * Displays a modal window with the specified ID.
 *
 * Checks if a modal element with the specified ID exists.
 * If the element exists, it initializes it as a Bootstrap modal and displays it.
 * If the element does not exist, an error is logged to the console.
 *
 * @private
 * @param {string} modalId - The ID of the modal element to show.
 * @returns {void}
 */
function showModal(modalId) {
    const modalElement = document.getElementById(modalId);

    if (!modalElement) {
        console.error(`${ERROR_MODAL_NOT_FOUND} with ID: ${modalId}`);
        return;
    }

    const allowBackdropClose = modalElement.dataset.backdropClose !== 'false';
    const vincarioModal = new Modal(modalElement, {
        backdrop: allowBackdropClose ? true : 'static',
        keyboard: allowBackdropClose,
    });

    modalElement.addEventListener('shown.bs.modal', () => {
        const firstInput = modalElement.querySelector('input:not([type="hidden"])');
        if (firstInput) {
            firstInput.focus();
        }
    });

    vincarioModal.show();
}

/**
 * Removes the existing modal element from the DOM if it exists and clears any leftover backdrop elements.
 *
 * @private
 * @param {string} modalId - The ID of the modal element to remove.
 * @returns {void}
 */
function removeExistingModal(modalId) {
    const existingModal = document.getElementById(modalId);

    if (existingModal) {
        // Hide the modal using Bootstrap's modal method
        const modalInstance = Modal.getInstance(existingModal);
        if (modalInstance) {
            modalInstance.hide();
        }

        // Remove the modal element from the DOM
        existingModal.remove();
    }

    // Remove any leftover modal-backdrop elements
    const backdrops = document.querySelectorAll('.modal-backdrop');
    backdrops.forEach(backdrop => backdrop.remove());
}

/**
 * Inserts the modal HTML into the document body.
 *
 * @private
 * @param {string} modalHtml - The HTML string representing the modal.
 * @returns {void}
 */
function insertModalHtml(modalHtml) {
    document.body.insertAdjacentHTML('beforeend', modalHtml);
}

/**
 * Shows a loading overlay with a delay while waiting for the modal to load.
 * If the modal loads quickly, the overlay is not shown.
 *
 * @param {number} delay - The delay in milliseconds before showing the overlay (default is 300ms).
 * @returns {void}
 */
function showLoadingOverlayWithDelay(delay = 300) {
    loadingTimeout = setTimeout(() => {
        if (!document.getElementById('loading-overlay')) {
            const overlay = document.createElement('div');
            overlay.id = 'loading-overlay';
            overlay.style.position = 'fixed';
            overlay.style.top = '0';
            overlay.style.left = '0';
            overlay.style.width = '100%';
            overlay.style.height = '100%';
            overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
            overlay.style.display = 'flex';
            overlay.style.justifyContent = 'center';
            overlay.style.alignItems = 'center';
            overlay.style.zIndex = '1050';
            overlay.innerHTML = `<div class="spinner-border text-light" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                 </div>`;
            document.body.appendChild(overlay);
        }
    }, delay);
}

/**
 * Hides the loading overlay and clears any existing timeout.
 *
 * @returns {void}
 */
function hideLoadingOverlay() {
    clearTimeout(loadingTimeout);
    const overlay = document.getElementById('loading-overlay');
    if (overlay) {
        overlay.remove();
    }
}

/**
 * Hides a modal window with the specified ID.
 *
 * If the modal element exists, it hides the modal using Bootstrap's modal methods.
 * If the modal does not exist, an error is logged to the console.
 *
 * @param {string} modalId - The ID of the modal element to hide.
 * @returns {void}
 */
export function hideModal(modalId = DEFAULT_MODAL_ID) {
    const modalElement = document.getElementById(modalId);

    if (!modalElement) {
        console.error(`${ERROR_MODAL_NOT_FOUND} with ID: ${modalId}`);
        return;
    }

    // Hide the modal using Bootstrap's modal method
    const modalInstance = Modal.getInstance(modalElement);
    if (modalInstance) {
        modalInstance.hide();
    } else {
        console.error(`Modal instance not found for ID: ${modalId}`);
    }
}
